import { Card, Chip, Divider, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import clsx from 'clsx';
import { get } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { getFullAddress } from '../../../common/utils/format-utils';
import { ApplicationStatus, AttestationStatus } from '../../../enums/application-status.enum';
import { dateFormat, useDialog } from '../../../hooks';
import { ApplicationsDialog } from '../applications-dialog';
import useStyles from './styles';

interface ApplicationsCardProps {
  className?: string;
  data: Record<string, any>;
}

export const ApplicationsCard: ComponentType<ApplicationsCardProps> = ({ className, data }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [openDetailsDialog] = useDialog(ApplicationsDialog);

  const {
    id,
    status,
    attestationStatus,
    isPassed,
    direction,
    attestationDate,
    attestationLocation,
    createdAt,
  } = data;

  const appStatus = (status: ApplicationStatus) => {
    switch (status) {
      case ApplicationStatus.CONFIRMED:
        return <Chip label={t('applicationStatus.confirmed', 'Одобрена')} color="success" />;
      case ApplicationStatus.PENDING:
        return (
          <Chip label={t('applicationStatus.pending', 'Ожидание одобрения')} color="warning" />
        );
      case ApplicationStatus.DECLINED:
        // case ApplicationStatus.FAILED:
        //   return <Chip label={t('applicationStatus.declined', 'Отклонена')} color="error" />;
        // case ApplicationStatus.STARTED:
        //   return <Chip label="В процессе" color="info" />;
        break;
      default:
        return <Chip label={t('applicationStatus.undefined', 'Не известно')} color="default" />;
    }
  };

  const appCompletedStatus = () => {
    if (attestationStatus === AttestationStatus.COMPLETED) {
      if (attestationStatus === AttestationStatus.ABSENT)
        return <Chip label={t('attestation.absent', 'Отсутствует')} color="error" />;
      if (isPassed) {
        return <Chip label={t('attestation.passed', 'Аттестация пройдена')} color="success" />;
      } else {
        return <Chip label={t('attestation.failed', 'Аттестация не пройдена')} color="error" />;
      }
    }
    return null;
  };

  const statuses = () => {
    return (
      <>
        {appStatus(status)}
        {appCompletedStatus()}
        {/*<Chip label="Ожидание оплаты" color="warning" />*/}
      </>
    );
  };

  const fullDate = (date: any, time: any) => t('date.full', `${date} в ${time}`, { date, time });

  const createdDate = createdAt ? dateFormat(new Date(createdAt), 'D F Y') : null;
  const createdTime = createdAt ? dateFormat(new Date(createdAt), 'T') : null;
  const createdFullDate = createdAt ? fullDate(createdDate, createdTime) : null;

  const startDate = attestationDate ? dateFormat(new Date(attestationDate), 'D F') : null;
  const startTime = attestationDate ? dateFormat(new Date(attestationDate), 'T') : null;
  const startFullDate = attestationDate ? fullDate(startDate, startTime) : null;

  const title = t('applications.attestation.title', `Заявка на аттестацию №${id}`, {
    id: data.taskId,
  });
  const text = get(direction, ['description', i18n.language], null);
  const location = attestationLocation ? getFullAddress(attestationLocation, i18n.language) : null;

  const handleDetails = () => {
    openDetailsDialog({
      statuses: statuses(),
      title,
      text,
      startDate,
      startTime,
      location,
      createdFullDate,
    });
  };

  return (
    <Card className={clsx(classes.root, className)} elevation={0}>
      <Box className={classes.header}>
        <Stack direction="row" spacing={1} mb={2} className={classes.statuses}>
          {statuses()}
        </Stack>
        {status === ApplicationStatus.DECLINED && (
          <Typography variant="subtitle3" component="p" color="error" mb={2}>
            {t('personalData.mismatch', 'Несовпадение личных данных')}
          </Typography>
        )}
        {/*TODO вывести дату, когда будет готов API, только для оплаченных*/}
        {attestationDate && (
          <Typography
            variant="body3"
            component="p"
            className={classes.additional}
            mb={2}
            color="secondary"
          >
            <Typography variant="subtitle3" component="span">
              {t('date.attestation', 'Дата аттестации')}:{' '}
            </Typography>
            {startFullDate}
          </Typography>
        )}
        <Typography variant="subtitle1" component="h5" mb={2}>
          {title}
        </Typography>
      </Box>
      {/*TODO вывести текст, когда будет готов API*/}
      <Typography className={classes.body} variant="body3" component="p">
        {text}
      </Typography>
      <Divider sx={{ mt: 1.5, mb: 1.5 }} />
      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        className={classes.footer}
        spacing={2}
      >
        <Typography variant="caption" color="grey.400" className={classes.date}>
          {createdFullDate}
        </Typography>

        <Typography onClick={handleDetails} variant="body3" className={classes.link}>
          {t('action.more', 'Подробнее')}...
        </Typography>
      </Stack>
    </Card>
  );
};
